/**
 * The SQLite-backed data source: `@kortex/pos-local` over the Android
 * bridge. Works identically with or without a network - nothing in this
 * file touches `fetch`.
 *
 * Error discipline (Phase 3 §30): LocalDataError messages are written for
 * the counter and pass through to the toast; anything else is logged with
 * its technical detail and surfaced as a calm, retryable message.
 */
import { toPaise } from '@kortex/shared';
import {
  LocalDataError,
  getInvoiceDto,
  getTicketDto,
  listInvoiceDtos,
  listMenuDtos,
  listStayDtos,
  listTableDtos,
  listTicketDtos,
  type LocalDatabase,
  type SyncDiagnostics,
  type SyncEngine,
  type SyncStatus,
} from '@kortex/pos-local';
import type {
  BillTicketInput,
  CreateTicketInput,
  InvoiceDto,
  InvoiceRowDto,
  MenuItemDto,
  PosDataSource,
  StayDto,
  TableDto,
  TicketDto,
} from './types';

export class LocalPosDataSource implements PosDataSource {
  readonly mode = 'local' as const;
  private readonly db: LocalDatabase;
  private readonly userId: string;
  private readonly syncEngine: SyncEngine | null;

  /**
   * Exposes the already-open database this source is running on, so
   * OfflineSetup.tsx can enrol/reset the offline PIN against the SAME
   * connection instead of opening a second one. This matters on the kiosk
   * specifically (always in 'local' mode - posData/index.ts's own file
   * doc): the OPFS SyncAccessHandle pool VFS this database sits on is
   * exclusive to one open handle per file, so a second `OpfsSqliteDriver`
   * pointed at the same file while this one is already open would fail to
   * open at all, not queue behind it.
   */
  get localDatabase(): LocalDatabase {
    return this.db;
  }

  constructor(db: LocalDatabase, userId: string, syncEngine: SyncEngine | null = null) {
    this.db = db;
    this.userId = userId;
    this.syncEngine = syncEngine;
  }

  getMenu(): Promise<MenuItemDto[]> {
    return guard(() => listMenuDtos(this.db));
  }

  listOpenTickets(): Promise<TicketDto[]> {
    return guard(() => listTicketDtos(this.db, 'OPEN'));
  }

  getTicket(id: string): Promise<TicketDto> {
    return guard(() => getTicketDto(this.db, id));
  }

  createTicket(input: CreateTicketInput): Promise<TicketDto> {
    return guard(async () => {
      const order = await this.db.orders.createOrder({
        type: input.type as never,
        covers: input.covers,
        tableId: input.tableId,
        stayId: input.stayId,
        userId: this.userId,
      });
      return getTicketDto(this.db, order.id);
    });
  }

  addLine(ticketId: string, input: { menuItemId: string; quantity: number }): Promise<TicketDto> {
    return guard(async () => {
      await this.db.orders.addLine(ticketId, {
        menuItemId: input.menuItemId,
        quantityMilli: Math.round(input.quantity * 1000),
        userId: this.userId,
      });
      return getTicketDto(this.db, ticketId);
    });
  }

  removeLine(ticketId: string, lineId: string): Promise<TicketDto> {
    return guard(async () => {
      await this.db.orders.removeLine(ticketId, lineId, this.userId);
      return getTicketDto(this.db, ticketId);
    });
  }

  billTicket(ticketId: string, input: BillTicketInput): Promise<InvoiceDto> {
    return guard(async () => {
      const result = await this.db.orders.settle(
        ticketId,
        {
          mode: input.mode as never,
          discountPct: input.discountPct,
          billDiscountPaise: toPaise(input.discount),
          discountReason: input.discountReason,
          userId: this.userId,
        },
        this.db.syncQueue,
      );
      return getInvoiceDto(this.db, result.invoiceId);
    });
  }

  voidTicket(ticketId: string, reason: string): Promise<void> {
    return guard(() => this.db.orders.voidOrder(ticketId, reason, this.userId));
  }

  listTables(): Promise<TableDto[]> {
    return guard(() => listTableDtos(this.db));
  }

  listStays(): Promise<StayDto[]> {
    return guard(() => listStayDtos(this.db));
  }

  listInvoices(options: {
    kind?: 'FNB' | 'ROOM';
    q?: string;
    /** First and last day to include, as YYYY-MM-DD. */
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<InvoiceRowDto[]> {
    return guard(() => listInvoiceDtos(this.db, options));
  }

  getInvoice(id: string): Promise<InvoiceDto> {
    return guard(() => getInvoiceDto(this.db, id));
  }

  voidInvoice(): Promise<void> {
    // Investigated in Phase 6 (Task #25 - filed as technical debt, not
    // silently changed): this is NOT simply stale now that Phase 4 sync
    // exists. Phase 4's own entity matrix (docs/offline/
    // PHASE-4-SYNC-ENTITY-MATRIX.md) tracks VOID_AGGREGATE as an OPEN
    // business decision for pre-sync order voids, and no wire operation for
    // voiding an already-settled, already-synced invoice - which requires
    // reversing an already-applied stock move under ADR-010's "server
    // derives stock, once" rule - has ever been defined. Building that
    // (a VOID_INVOICE sync operation, server-side idempotent stock
    // reversal, and a decision on what a stale local view should show in
    // the meantime) is new protocol design, out of scope for a rejection-
    // message fix. Still deliberately online-only; the message below no
    // longer implies this resolves itself once syncing exists.
    return Promise.reject(
      new Error(
        'Voiding an invoice needs the server - there is no offline mechanism for ' +
          'reversing the stock this invoice already deducted. Reconnect and try again.',
      ),
    );
  }

  async pendingSyncCount(): Promise<number | null> {
    const counts = await this.db.syncQueue.counts();
    return counts.PENDING + counts.SENT;
  }

  async getSyncStatus(): Promise<SyncStatus | null> {
    if (!this.syncEngine) return null;
    return this.syncEngine.status();
  }

  async syncNow(): Promise<void> {
    if (!this.syncEngine) return;
    await this.syncEngine.syncNow();
  }

  async getSyncDiagnostics(): Promise<SyncDiagnostics | null> {
    if (!this.syncEngine) return null;
    return this.syncEngine.diagnostics();
  }
}

async function guard<T>(operation: () => Promise<T>): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    if (error instanceof LocalDataError) {
      // Messages are already written for the counter; keep the code visible
      // to diagnostics without leaking SQL to the cashier.
      console.error(`[pos-local] ${error.code}: ${error.message}`);
      throw new Error(error.message);
    }
    console.error('[pos-local] unexpected database failure', error);
    throw new Error('The device database could not complete that. Please try again.');
  }
}
